/**
 * Motion for every dropdown list, without the animation that flashed.
 *
 * WHY NOT HEROUI'S OWN ANIMATION. HeroUI animates a popover with framer-motion,
 * and at the end of that animation there is one frame in which the animation
 * has been removed but its final value not yet written: the list went
 * invisible for a frame after opening and came back for a frame after closing
 * (UX follow-up, decision D191). Every list therefore runs with
 * `disableAnimation`, which made opening and closing feel abrupt.
 *
 * WHAT REPLACES IT (decision D196):
 *
 *  - OPENING is a CSS keyframe on the list's own box (`POPOVER_MOTION_CLASS`,
 *    `styles.css`): opacity and a 4px slide, 150ms. A CSS animation is resolved
 *    by the style engine, so there is no hand-over frame between "animating"
 *    and "done"; its last keyframe is the box's resting style.
 *  - CLOSING cannot be done by the list itself: HeroUI unmounts it at once, and
 *    keeping it mounted would delay focus returning to the field and the
 *    choice being applied. The real list therefore goes IMMEDIATELY — the
 *    selection, focus and ARIA state change in the same frame — and a copy of
 *    what was on screen fades out in its place. The copy is decoration only:
 *    inert, hidden from assistive technology, with no ids or roles, so nothing
 *    can find it, focus it or click it. It is removed when its fade ends.
 *  - REDUCED MOTION: no copy is made, and the stylesheet turns the keyframes
 *    off, so a list simply appears and disappears, as before.
 */

/** Put on the visible box of a popover (a list, a menu, a calendar). */
export const POPOVER_MOTION_CLASS = 'lims-pop';

/** Marks the fading copy of a list that has just closed. */
export const POPOVER_GHOST_CLASS = 'lims-pop-ghost';

/** Removes a copy whose `animationend` never arrives (a hidden tab, say). */
const GHOST_FALLBACK_MS = 400;

/** Attributes a copy must not carry: it must not be found, named or focused. */
const GHOST_STRIPPED = ['id', 'role', 'tabindex', 'data-react-aria-top-layer', 'aria-labelledby', 'aria-describedby', 'aria-controls'];

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/** A decorative copy of a removed popover, or null when it is not one. */
export function popoverGhost(node: Node): HTMLElement | null {
  if (!(node instanceof HTMLElement) || node.classList.contains(POPOVER_GHOST_CLASS)) return null;
  if (!node.classList.contains(POPOVER_MOTION_CLASS) && !node.querySelector(`.${POPOVER_MOTION_CLASS}`)) {
    return null;
  }
  const ghost = node.cloneNode(true) as HTMLElement;
  for (const element of [ghost, ...Array.from(ghost.querySelectorAll('*'))]) {
    for (const name of GHOST_STRIPPED) element.removeAttribute(name);
  }
  ghost.classList.add(POPOVER_GHOST_CLASS);
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  return ghost;
}

/**
 * Watches the layer popovers are portalled into and fades out every popover
 * removed from it. Returns a function that stops watching. Installed once, at
 * start-up, so every Select, menu and calendar gets the same motion.
 */
export function installPopoverExitMotion(layer: HTMLElement = document.body): () => void {
  if (typeof MutationObserver === 'undefined') return () => undefined;
  const observer = new MutationObserver((records) => {
    if (prefersReducedMotion()) return;
    for (const record of records) {
      for (const removed of Array.from(record.removedNodes)) {
        const ghost = popoverGhost(removed);
        if (!ghost) continue;
        let gone = false;
        const drop = () => {
          if (gone) return;
          gone = true;
          ghost.remove();
        };
        ghost.addEventListener('animationend', drop);
        window.setTimeout(drop, GHOST_FALLBACK_MS);
        layer.appendChild(ghost);
      }
    }
  });
  observer.observe(layer, { childList: true });
  return () => observer.disconnect();
}
